import { useCallback, useEffect, useState } from 'react';

import { useNavigation } from '@react-navigation/native';

import { CasinoGame } from './use-casino-games';
import { useCasinoSession } from './use-casino-session';

/**
 * Formats the casino game launch URL with required query parameters
 * @param launchUrl - The base launch URL for the casino game
 * @param sessionId - The casino session ID from /auth/casino/session API
 * @returns Formatted URL with language, and _sid parameters
 */
const formatCasinoGameUrl = (launchUrl: string, sessionId: string): string => {
    const url = `${launchUrl}?language=en&_sid=${sessionId}`;
    return url;
};

/**
 * Hook to format the casino game launch URL with session ID
 * Fetches the session ID directly from the casino session endpoint
 * @param launchUrl - The base launch URL for the casino game
 * @returns Formatted URL or null if session ID is not available
 */
export const useFormattedCasinoGameUrl = (launchUrl: string | undefined) => {
    const { data } = useCasinoSession();
    const sessionId = data?.data.session_id;
    const formattedUrl = sessionId && launchUrl ? formatCasinoGameUrl(launchUrl, sessionId) : null;

    return formattedUrl;
};

/**
 * Hook to start a casino game with loading state management
 * Navigates to the CasinoGameWebView screen which handles URL formatting and loading
 * @returns Object with handleStartGame function and isLoading state
 */
export const useStartCasinoGameUrl = ({ id, name, launchUrl }: Pick<CasinoGame, 'id' | 'name' | 'launchUrl'>) => {
    const navigation = useNavigation();
    const [isNavigating, setIsNavigating] = useState(false);

    // Navigate to the screen which will handle fetching and formatting the URL
    useEffect(() => {
        if (isNavigating) {
            navigation.navigate('CasinoGameWebView', {
                gameId: id,
                gameParams: {
                    launchUrl,
                    title: name,
                },
            });
            setIsNavigating(false);
        }
    }, [isNavigating, id, launchUrl, name, navigation]);

    const handleStartGame = useCallback(() => {
        setIsNavigating(true);
    }, []);

    return {
        handleStartGame,
        isLoading: isNavigating,
    };
};
